import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { ToastContainer } from '../components/ui/index.js';
import { ProtectedRoute } from '../guards/ProtectedRoute.js';
import { AdminRoute } from '../guards/AdminRoute.js';
import { Landing } from '../pages/Landing.js';
import { StudioLayout } from '../components/studio/index.js';
import { StudioHome } from '../pages/studio/StudioHome.js';
import { EditorPage } from '../pages/studio/EditorPage.js';
import { AIStudio } from '../pages/studio/AIStudio.js';
import { AlbumDetail } from '../pages/studio/AlbumDetail.js';
import { Invitations } from '../pages/studio/Invitations.js';
import { AdminLayout } from '../components/admin/index.js';
import { AdminDashboard } from '../pages/admin/AdminDashboard.js';
import { AdminUsers } from '../pages/admin/AdminUsers.js';
import { AdminHistory } from '../pages/admin/AdminHistory.js';
import { AdminAIModels } from '../pages/admin/AdminAIModels.js';
import { AdminPayments } from '../pages/admin/AdminPayments.js';
import { AdminCreditTransactions } from '../pages/admin/AdminCreditTransactions.js';
import { AdminSettings } from '../pages/admin/AdminSettings.js';
import { AdminAITestGen } from '../pages/admin/AdminAITestGen.js';
import { AdminPromptBlocks } from '../pages/admin/AdminPromptBlocks.js';
import { SettingsPage } from '../pages/studio/SettingsPage.js';
import { InvitationViewer } from '../pages/InvitationViewer.js';

// ============================================================
// Placeholder Pages
// ============================================================

function AuthCallback() {
  return <Navigate to="/studio" replace />;
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{title}</h2>
      <p style={{ marginTop: '0.5rem' }}>준비 중입니다</p>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 700 }}>404</h1>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>페이지를 찾을 수 없습니다</p>
      </div>
    </div>
  );
}

// ============================================================
// Root Layout — ToastContainer를 Router context 내부에 마운트
// ============================================================

function RootLayout() {
  return (
    <>
      <Outlet />
      <ToastContainer />
    </>
  );
}

// ============================================================
// Router
// ============================================================

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public
      { path: '/', element: <Landing /> },
      { path: '/auth-callback', element: <AuthCallback /> },

      // 기존 /dashboard → /studio 리다이렉트
      { path: '/dashboard', element: <Navigate to="/studio" replace /> },

      // Protected — Studio
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <StudioLayout />,
            children: [
              { path: '/studio', element: <StudioHome /> },
              { path: '/studio/ai', element: <AIStudio /> },
              { path: '/studio/ai/albums/:id', element: <AlbumDetail /> },
              { path: '/studio/invitations', element: <Invitations /> },
              { path: '/studio/settings', element: <SettingsPage /> },
            ],
          },
          // 에디터는 StudioLayout 밖에서 전체 화면으로 표시
          { path: '/studio/editor/:id', element: <EditorPage /> },
        ],
      },

      // Admin
      {
        path: '/admin',
        element: <AdminRoute />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <AdminDashboard /> },
              { path: 'users', element: <AdminUsers /> },
              { path: 'history', element: <AdminHistory /> },
              { path: 'ai-models', element: <AdminAIModels /> },
              { path: 'ai-test', element: <AdminAITestGen /> },
              { path: 'prompt-blocks', element: <AdminPromptBlocks /> },
              { path: 'payments', element: <AdminPayments /> },
              { path: 'credit-transactions', element: <AdminCreditTransactions /> },
              { path: 'settings', element: <AdminSettings /> },
            ],
          },
        ],
      },

      // Public views (청첩장 공개 뷰어)
      { path: '/inv/:slugOrId', element: <InvitationViewer /> },

      // 404
      { path: '*', element: <NotFound /> },
    ],
  },
]);
